import { Injectable, inject } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { languages } from '../consts/languages';
import { LanguageCode } from '../models/language';

const STORAGE_KEY = 'app_lang';
const SUPPORTED = languages.map((l) => l.value);
const DEFAULT_LANG: LanguageCode = 'pt';

type LanguageVm = (typeof languages)[number];

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly translate = inject(TranslateService);
  private readonly _langCode$ = new BehaviorSubject<LanguageCode>(DEFAULT_LANG);

  readonly langCode$: Observable<LanguageCode> = this._langCode$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

  readonly language$: Observable<LanguageVm> = this.langCode$.pipe(
    map(
      (code) =>
        languages.find((l) => l.value === code) ?? languages.find((l) => l.value === DEFAULT_LANG)!,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    this.translate.addLangs(SUPPORTED);

    const fromStorage = localStorage.getItem(STORAGE_KEY) as LanguageCode | null;
    const normalized = this.normalizeCode(fromStorage || DEFAULT_LANG);

    this.translate.setFallbackLang(DEFAULT_LANG);
    this.translate.use(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    this._langCode$.next(normalized);

    this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
      const code = this.normalizeCode(e.lang);
      localStorage.setItem(STORAGE_KEY, code);
      this._langCode$.next(code);
    });
  }

  private normalizeCode(code: string): LanguageCode {
    return SUPPORTED.includes(code as LanguageCode) ? (code as LanguageCode) : DEFAULT_LANG;
  }

  setLanguage(code: LanguageCode): void {
    const normalized = this.normalizeCode(code);
    if (this.translate.getCurrentLang() === normalized) return;

    this.translate.use(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    this._langCode$.next(normalized);
  }

  getSupported(): string[] {
    return SUPPORTED;
  }

  instant(key: string, params?: Record<string, any>): string {
    return this.translate.instant(key, params);
  }

  get(key: string, params?: Record<string, any>) {
    return this.translate.get(key, params);
  }

  get current(): LanguageCode {
    return (this.translate.getCurrentLang() as LanguageCode) || DEFAULT_LANG;
  }

  readonly currentLang$ = merge(
    of(this.translate.getCurrentLang() as LanguageCode),
    this.translate.onLangChange.pipe(map((e) => e.lang as LanguageCode)),
  ).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

  readonly currentLanguage$ = this.currentLang$.pipe(
    map(
      (code) =>
        languages.find((l) => l.value === code) ?? languages.find((l) => l.value === DEFAULT_LANG)!,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
}
